/**
 * The board menu — the ☰ at the left of the header (docs/06-UI-SPEC.md §6.1).
 *
 * The teacher's alone, and it holds the things done to the lesson rather than to the
 * board: for now that is ending it. It is a menu rather than a button in the header
 * because End Session is pressed once a lesson and cannot be taken back, and a
 * control like that should not sit under a thumb reaching for the pen.
 */

import { useEffect, useState } from 'react';

export interface BoardMenuProps {
  /** SRS I1 — opens the confirmation. The menu itself never ends anything. */
  readonly onEndSession: () => void;
}

function MenuIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M3.75 7.5h16.5M3.75 12h16.5M3.75 16.5h16.5" />
    </svg>
  );
}

interface OpenMenuProps {
  readonly onEndSession: () => void;
  readonly onClose: () => void;
}

/** The list itself, and the sheet behind it that closes it on a tap anywhere else. */
function OpenMenu({ onEndSession, onClose }: OpenMenuProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-40" onPointerDown={onClose} />
      <div
        role="menu"
        aria-label="Board"
        className="absolute left-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg"
      >
        <button
          type="button"
          role="menuitem"
          onClick={onEndSession}
          className="flex min-h-touch w-full items-center px-3 text-left text-sm text-danger transition-colors duration-fast hover:bg-surface-hover"
        >
          End Session
        </button>
      </div>
    </>
  );
}

export function BoardMenu({ onEndSession }: BoardMenuProps) {
  const [open, setOpen] = useState(false);
  const close = (): void => {
    setOpen(false);
  };

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label="Board menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
        className="flex h-touch w-touch items-center justify-center rounded-md text-text-secondary transition-colors duration-fast hover:bg-surface-hover hover:text-text-primary"
      >
        <MenuIcon />
      </button>
      {open ? (
        <OpenMenu
          onClose={close}
          onEndSession={() => {
            close();
            onEndSession();
          }}
        />
      ) : null}
    </div>
  );
}
